/**
 * Doubt: convince, don't chip-kill.
 *
 * Onion model:
 * - Outer shells: Faith/Wisdom convert; wrong Answers/chips revert or tangle; bulk chip OK
 * - Core: never multi-chipped; only a direct Faith/Wisdom Answer or Redeem finishes
 * - Full conviction → presence settles (silence / Redeem path)
 * - Incomplete Faith outers re-doubt over time when decay is enabled (Still pauses)
 *
 * Scaffold (teaching):
 * - core: outerCount 0, decay off — Redeem/Faith settles immediately
 * - Wrong Word grows an outer shell and enables the re-doubt timer
 * - half / full: spawn already tangled with decay on
 */
import type { Category, LieType } from "../board/types";
import type { Presence, VerseDef } from "../cards/types";

/** How tangled Doubt is when it enters the board. */
export type DoubtScaffold = "core" | "half" | "full";

export interface DoubtState {
  /** Outer shells currently on the onion (0 = core only). */
  outerCount: number;
  /** Cap for tangle growth (usually 2). */
  maxOuterCount: number;
  /** How many outers are currently Faith (0 … outerCount). */
  outerFaith: number;
  /** Seconds accumulated toward one outer re-doubt while mixed. */
  decayAcc: number;
  /**
   * When false, incomplete Faith does not re-doubt (G1 core-only until tangled).
   * Enabled on half/full spawn, or after the first wrong-Word tangle.
   */
  decayEnabled: boolean;
}

/** Seconds of mixed conviction before one outer Faith reverts. */
export const DOUBT_DECAY_SECONDS = 5;

export function isDoubtPresence(p: Presence | null | undefined): boolean {
  return !!p && p.side === "enemy" && (p.lieType === "doubt" || !!p.doubt);
}

/**
 * @param maxLevel Lie family max intensity (usually 3) — sets max outer shells.
 * @param scaffold core = teach; half = 1 outer; full = full onion (default).
 */
export function initDoubtState(
  maxLevel: number,
  scaffold: DoubtScaffold = "full",
): DoubtState {
  const maxOuterCount = Math.max(0, Math.min(2, Math.round(maxLevel) - 1));
  if (scaffold === "core") {
    return {
      outerCount: 0,
      maxOuterCount,
      outerFaith: 0,
      decayAcc: 0,
      decayEnabled: false,
    };
  }
  if (scaffold === "half") {
    const outerCount = Math.min(1, maxOuterCount);
    return {
      outerCount,
      maxOuterCount,
      outerFaith: 0,
      decayAcc: 0,
      decayEnabled: true,
    };
  }
  // full
  return {
    outerCount: maxOuterCount,
    maxOuterCount,
    outerFaith: 0,
    decayAcc: 0,
    decayEnabled: true,
  };
}

/** Rings still in Doubt (outers open + core). Core is always present until settled. */
export function doubtRemaining(d: DoubtState): number {
  return d.outerCount - d.outerFaith + 1;
}

/** Outers still in Doubt. */
export function doubtOutersOpen(d: DoubtState): number {
  return Math.max(0, d.outerCount - d.outerFaith);
}

/** Room to grow another outer shell via wrong Word. */
export function doubtCanTangle(d: DoubtState): boolean {
  return d.outerCount < d.maxOuterCount;
}

/** Core is exposed: all outers Faith (or no outers) — ready for Answer / Redeem. */
export function doubtCoreExposed(d: DoubtState): boolean {
  return d.outerFaith >= d.outerCount;
}

/** Conviction progress for UI. */
export function doubtFaithProgress(d: DoubtState): {
  faith: number;
  total: number;
} {
  return { faith: d.outerFaith, total: d.outerCount + 1 };
}

/**
 * Faith and Wisdom persuade Doubt.
 * strongVs:["doubt"] also counts (future / special verses).
 */
export function convincesDoubt(
  category: Category,
  lieType: LieType | undefined,
  strongVs?: readonly LieType[],
): boolean {
  if (lieType !== "doubt") return false;
  if (category === "faith" || category === "wisdom") return true;
  if (strongVs?.includes("doubt")) return true;
  return false;
}

export type DoubtHitKind = "direct" | "chip";

export interface DoubtHitResult {
  action: "convert" | "revert" | "tangle" | "noop" | "complete";
  outerFaithBefore: number;
  outerFaithAfter: number;
  outerCountBefore: number;
  outerCountAfter: number;
  completed: boolean;
  ringsChanged: number;
  summary: string;
}

function cloneDoubt(d: DoubtState): DoubtState {
  return {
    outerCount: d.outerCount,
    maxOuterCount: d.maxOuterCount,
    outerFaith: d.outerFaith,
    decayAcc: d.decayAcc,
    decayEnabled: d.decayEnabled,
  };
}

function hitResult(
  partial: Omit<
    DoubtHitResult,
    "outerFaithBefore" | "outerCountBefore" | "outerFaithAfter" | "outerCountAfter"
  > & {
    outerFaithBefore: number;
    outerCountBefore: number;
    state: DoubtState;
  },
): { state: DoubtState; result: DoubtHitResult } {
  const { state, ...rest } = partial;
  return {
    state,
    result: {
      ...rest,
      outerFaithAfter: state.outerFaith,
      outerCountAfter: state.outerCount,
    },
  };
}

/**
 * Apply one Answer or layer-chip interaction to a Doubt onion.
 * @param strength How many rings to flip (chip can multi-outer; never finishes core via chip)
 */
export function applyDoubtHit(
  d: DoubtState,
  opts: {
    meets: boolean;
    strength: number;
    kind: DoubtHitKind;
  },
): { state: DoubtState; result: DoubtHitResult } {
  const strength = Math.max(1, Math.floor(opts.strength));
  const outerFaithBefore = d.outerFaith;
  const outerCountBefore = d.outerCount;
  let state = cloneDoubt(d);
  state.decayAcc = 0;

  if (opts.meets) {
    if (opts.kind === "chip") {
      const open = doubtOutersOpen(state);
      if (open <= 0) {
        return hitResult({
          state,
          action: "noop",
          outerFaithBefore,
          outerCountBefore,
          completed: false,
          ringsChanged: 0,
          summary:
            state.outerCount === 0
              ? "Core is open — use a direct Answer or Redeem (chip cannot settle it)"
              : "Core still holds — chip cannot settle the real question",
        });
      }
      const n = Math.min(strength, open);
      state.outerFaith += n;
      return hitResult({
        state,
        action: "convert",
        outerFaithBefore,
        outerCountBefore,
        completed: false,
        ringsChanged: n,
        summary:
          n === 1
            ? "Outer question turns toward Faith"
            : `${n} outer questions turn toward Faith`,
      });
    }

    // Direct Answer — peel-first:
    // Core may only be settled when it is already exposed *before* this hit.
    // Leftover snipe strength never pierces into the core on the same play
    // (e.g. Ps 56:3 micro must not one-shot a full III onion).
    const exposedAtStart = doubtCoreExposed(state);

    if (exposedAtStart) {
      // Core open (or core-only scaffold): one direct Faith settles
      return hitResult({
        state,
        action: "complete",
        outerFaithBefore,
        outerCountBefore,
        completed: true,
        ringsChanged: 1,
        summary: "Core settled in Faith",
      });
    }

    // Shells still closed — only convert outers this hit
    const open = doubtOutersOpen(state);
    const takeOuter = Math.min(strength, open);
    if (takeOuter <= 0) {
      return hitResult({
        state,
        action: "noop",
        outerFaithBefore,
        outerCountBefore,
        completed: false,
        ringsChanged: 0,
        summary: "No change",
      });
    }
    state.outerFaith += takeOuter;
    const nowOpen = doubtCoreExposed(state);
    return hitResult({
      state,
      action: "convert",
      outerFaithBefore,
      outerCountBefore,
      completed: false,
      ringsChanged: takeOuter,
      summary:
        takeOuter === 1
          ? nowOpen
            ? "Outer turns toward Faith — core open (Answer or Redeem)"
            : "Outer question turns toward Faith"
          : nowOpen
            ? `${takeOuter} outers turn toward Faith — core open (Answer or Redeem)`
            : `${takeOuter} outer questions turn toward Faith`,
    });
  }

  // --- Wrong Word ---
  // 1) Undo Faith on outers if any
  // 2) Else tangle: grow outer shells and enable re-doubt timer
  if (state.outerFaith > 0) {
    const n = Math.min(strength, state.outerFaith);
    state.outerFaith -= n;
    return hitResult({
      state,
      action: "revert",
      outerFaithBefore,
      outerCountBefore,
      completed: false,
      ringsChanged: n,
      summary:
        n === 1
          ? "Outer Faith slips back into Doubt"
          : `${n} outer Faith rings slip back into Doubt`,
    });
  }

  if (doubtCanTangle(state)) {
    const grow = Math.min(strength, state.maxOuterCount - state.outerCount);
    state.outerCount += grow;
    state.decayEnabled = true;
    state.outerFaith = 0;
    return hitResult({
      state,
      action: "tangle",
      outerFaithBefore,
      outerCountBefore,
      completed: false,
      ringsChanged: grow,
      summary:
        grow === 1
          ? "Wrong Word tangles the question — an outer shell forms"
          : `Wrong Word tangles the question — ${grow} outer shells form`,
    });
  }

  return hitResult({
    state,
    action: "noop",
    outerFaithBefore,
    outerCountBefore,
    completed: false,
    ringsChanged: 0,
    summary: "Wrong Word finds no Faith to undo — question still open",
  });
}

/** One outer re-doubt tick (decay). No-op if decay disabled. */
export function applyDoubtDecay(d: DoubtState): {
  state: DoubtState;
  reverted: boolean;
} {
  if (!d.decayEnabled || d.outerFaith <= 0) {
    return { state: { ...d, decayAcc: 0 }, reverted: false };
  }
  return {
    state: {
      ...d,
      outerFaith: d.outerFaith - 1,
      decayAcc: 0,
    },
    reverted: true,
  };
}

/**
 * How many rings a verse tries to flip on Doubt.
 * Chip: flat answerLevels (+ empower already folded by caller).
 * Direct: same ladder as answer steps (meet / micro aim), min 1 on miss for revert/tangle.
 */
export function doubtHitStrength(
  def: Pick<VerseDef, "answerLevels" | "snipeTier" | "category" | "strongVs">,
  opts: {
    kind: DoubtHitKind;
    meets: boolean;
    correctTarget?: boolean;
    empowerBonus?: number;
  },
): number {
  const base = Math.max(1, def.answerLevels ?? 1);
  const empower = Math.max(0, opts.empowerBonus ?? 0);

  if (opts.kind === "chip") {
    return Math.min(3, base + empower);
  }

  // Soften: always one ring (convert if meets, else revert/tangle)
  if (def.snipeTier === "soften") {
    return 1 + (empower > 0 ? 1 : 0);
  }

  if (!opts.meets) {
    return 1;
  }

  let steps = base + 1; // meet
  if (opts.correctTarget && def.snipeTier === "micro") {
    steps += 1;
  }
  steps += empower;
  const cap = def.snipeTier === "micro" ? 3 : 2;
  return Math.min(cap, steps);
}
